import { memo, useCallback, useRef, useEffect } from 'react'
import { ChatBubbleLeftRightIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { trackButtonClick } from '../../utils/analytics'

/**
 * ChannelsSidebar - Left sidebar showing list of user's channels
 * Supports user/admin tabs and filtering
 * Memoized for performance - only re-renders when props actually change
 */
const ChannelsSidebar = memo(function ChannelsSidebar({
  showChannelsList,
  activeTab,
  isAdmin,
  userChannels,
  supportChannels,
  showOnlyNeedsReply,
  filteredChannels,
  activeChannel,
  needsReplyCount,
  onToggleSidebar,
  onSwitchTab,
  onToggleNeedsReply,
  onSelectChannel,
  getChannelDisplayName
}) {
  const { t } = useTranslation()
  const scrollContainerRef = useRef(null)
  
  // Determine if we're showing support channels (admin tab)
  const isShowingSupportChannels = activeTab === 'admin'

  if (!showChannelsList) {
    const collapsedCount = (userChannels?.length || 0) + (isAdmin ? (supportChannels?.length || 0) : 0)
    return (
      <div className="w-10 border-r border-gray-200 flex flex-col flex-shrink-0 bg-gray-50 items-center pt-3 gap-3">
        <button
          onClick={() => {
            trackButtonClick('show_channels_list', 'channels_tab')
            onToggleSidebar(true)
          }}
          className="p-1.5 bg-primary hover:bg-primary-600 rounded-lg transition-colors shadow-sm"
          title={t('channels.showChannels')}
        >
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {collapsedCount > 0 && (
          <span className="text-xs font-semibold text-gray-500 bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center">
            {collapsedCount > 9 ? '9+' : collapsedCount}
          </span>
        )}
        {needsReplyCount > 0 && (
          <span className="text-xs font-semibold text-white bg-red-500 rounded-full w-6 h-6 flex items-center justify-center" title={t('channels.needsReply')}>
            {needsReplyCount > 9 ? '9+' : needsReplyCount}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="w-44 border-r border-gray-200 flex flex-col flex-shrink-0">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-900">{t('channels.title')}</h3>
          <button
            onClick={() => {
              trackButtonClick('hide_channels', 'channels_tab')
              onToggleSidebar(false)
            }}
            className="p-1.5 bg-primary hover:bg-primary-600 rounded-lg transition-colors shadow-sm"
            title={t('channels.hideChannels')}
          >
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded">
          <button
            onClick={() => {
              trackButtonClick('switch_tab', 'channels_tab', { tab: 'user', channel_count: userChannels.length })
              onSwitchTab('user')
            }}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${
              activeTab === 'user'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('channels.myChannels')}
          </button>
          <button
            onClick={() => {
              trackButtonClick('switch_tab', 'channels_tab', { tab: 'admin', needs_reply_count: needsReplyCount })
              onSwitchTab('admin')
            }}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors relative min-w-0 ${
              activeTab === 'admin'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="truncate">{t('channels.support')}</span>
            {needsReplyCount > 0 && (
              <span className="absolute -top-1.5 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full leading-none">
                {needsReplyCount > 99 ? '99+' : needsReplyCount}
              </span>
            )}
          </button>
        </div>
      </div>
      
      {/* Filter for admin tab */}
      {isAdmin && activeTab === 'admin' && (
        <div className="flex-shrink-0 border-b border-gray-200 px-3 py-2">
          <button
            onClick={() => {
              trackButtonClick('toggle_needs_reply_filter', 'channels_tab', { is_active: !showOnlyNeedsReply, needs_reply_count: needsReplyCount })
              onToggleNeedsReply()
            }}
            className={`w-full px-2 py-1 text-xs rounded ${
              showOnlyNeedsReply 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('channels.needsReplyOnly')} ({needsReplyCount})
          </button>
        </div>
      )}
      
      {/* Channel List */}
      <div ref={scrollContainerRef} className="py-2">
        {/* Info banner for support channels */}
        {isShowingSupportChannels && filteredChannels.length > 0 && (
          <div className="mx-2 mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <UserGroupIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800 leading-relaxed">
                {t('channels.supportChannelsInfo', { 
                  defaultValue: 'Collaborative channels - all admins can join and respond' 
                })}
              </p>
            </div>
          </div>
        )}
        
        <div className="space-y-1 px-2">
          {filteredChannels.map(ch => (
            <ChannelListItem
              key={`channel-${ch.id}`}
              channel={ch}
              isActive={activeChannel?.id === ch.id}
              onSelect={onSelectChannel}
              getDisplayName={getChannelDisplayName}
              isSupportChannel={isShowingSupportChannels}
            />
          ))}
          {filteredChannels.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-xs">{t('channels.noChannelsInTab')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

/**
 * Individual channel list item
 * Memoized for performance since channels rarely change
 * Custom comparison prevents re-renders when only array reference changes
 */
const ChannelListItem = memo(function ChannelListItem({ channel, isActive, onSelect, getDisplayName, isSupportChannel }) {
  const buttonRef = useRef(null)
  const lastActiveChannelIdRef = useRef(null)
  
  const handleClick = useCallback(() => {
    trackButtonClick('select_channel', 'channels_tab', { 
      channel_id: channel.id, 
      channel_type: channel.channelType,
      had_unread: channel.unreadCount > 0,
      is_support_channel: isSupportChannel
    })
    onSelect(channel)
  }, [channel, isSupportChannel, onSelect])
  
  // Scroll into view when this channel becomes active
  // Only scroll if we're activating a DIFFERENT channel (not on re-renders of same active channel)
  useEffect(() => {
    if (isActive && lastActiveChannelIdRef.current !== channel.id && buttonRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        if (buttonRef.current && isActive) {
          buttonRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          })
        }
      })
      lastActiveChannelIdRef.current = channel.id
    } else if (!isActive && lastActiveChannelIdRef.current === channel.id) {
      // Reset when this channel becomes inactive
      lastActiveChannelIdRef.current = null
    }
  }, [isActive, channel.id])

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className={`w-full text-left px-2 py-2 rounded text-sm transition-colors scroll-mt-2 ${
        isActive
          ? isSupportChannel
            ? 'bg-blue-600 text-white shadow-md'
            : 'bg-primary text-white'
          : channel.needsReply
          ? 'bg-yellow-50 hover:bg-yellow-100 text-gray-900'
          : isSupportChannel
          ? 'bg-blue-50 hover:bg-blue-100 text-gray-900 border border-blue-200'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <div className="flex items-start gap-2">
        {isSupportChannel ? (
          <UserGroupIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        ) : (
          <ChatBubbleLeftRightIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          {isSupportChannel && channel.ownerEmail && channel.ownerFirstName ? (
            <>
              <div className="font-medium truncate">{`${channel.ownerFirstName} ${channel.ownerLastName}`}</div>
              <div className="text-xs opacity-75 truncate">{channel.ownerEmail}</div>
            </>
          ) : (
            <>
              <div className="font-medium truncate">{getDisplayName(channel)}</div>
              {channel.lastMessageUser && (
                <div className="text-xs opacity-75 truncate mt-0.5">
                  {channel.lastMessageUser}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {channel.needsReply && (
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          )}
          {channel.unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {channel.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these specific props change
  // This prevents re-renders when parent re-renders but these values are the same
  return (
    prevProps.channel.id === nextProps.channel.id &&
    prevProps.channel.unreadCount === nextProps.channel.unreadCount &&
    prevProps.channel.needsReply === nextProps.channel.needsReply &&
    prevProps.channel.name === nextProps.channel.name &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isSupportChannel === nextProps.isSupportChannel
  )
})

export default ChannelsSidebar
